import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { toast } from "sonner";
import { Gauge, IndianRupee, TrendingUp, Wallet, Landmark, Package, Percent } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import {
  AggHeader, PeriodSelect, ResetSampleButton, Kpi, Panel, inr, inrCompact, num, pct, useSort, SortTh, TH_ROW, TD,
  STATUS_STYLE,
} from "./aggKit";

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-[#0B1220] text-white text-xs border border-[#26324d] px-3 py-2 space-y-0.5">
      <div className="font-bold">{new Date(label).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</div>
      <div className="text-orange-300">Gross revenue: {inr(p.revenue)}</div>
      <div className="text-emerald-300">Net profit: {inr(p.profit)}</div>
      <div className="text-slate-300">Margin: {pct(p.margin_pct)} · {num(p.orders)} orders</div>
    </div>
  );
}

export default function AggOverviewPage() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/agg/overview", { params: { days } })
      .then((r) => setD(r.data))
      .catch((e) => toast.error(apiErr(e)))
      .finally(() => setLoading(false));
  }, [days]);
  useEffect(() => { load(); }, [load, tick]);

  const remit = async (m) => {
    const utr = window.prompt(`Remit ${inr(m.outstanding)} COD (${m.parcels} parcels) to ${m.merchant_name}.\nEnter bank UTR / reference (leave blank to auto-generate):`, "");
    if (utr === null) return;
    try {
      const r = await api.post("/agg/cod/remit", { merchant_id: m.merchant_id, utr });
      toast.success(`Remitted ${inr(r.data.amount)} · ${r.data.utr}`);
      setTick((t) => t + 1);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const sc = useSort(d?.carrier_scorecard || [], "shipments");
  const cod = d?.cod;
  const codTotal = cod ? cod.outstanding + cod.remitted_total : 0;
  const remPct = codTotal ? (cod.remitted_total / codTotal) * 100 : 0;
  const maxAging = Math.max(1, ...(cod?.aging || []).map((a) => a.amount));

  return (
    <div className="space-y-4" data-testid="agg-overview-page">
      <AggHeader
        testid="agg-overview-header"
        icon={Gauge}
        title="Platform Overview"
        subtitle="GMV, revenue, profit and COD across every MSME, 3PL carrier and franchise hub."
        actions={<><PeriodSelect value={days} onChange={setDays} /><ResetSampleButton onDone={() => setTick((t) => t + 1)} /></>}
      />

      {!d ? (
        <div className="text-muted-foreground text-sm p-6">{loading ? "Loading platform metrics…" : "No data"}</div>
      ) : (
        <>
          <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 ${loading ? "opacity-60" : ""}`}>
            <Kpi testid="kpi-gmv" label="Total GMV (cumulative)" icon={Package} value={inrCompact(d.gmv.total)}
              sub={<span>{num(d.gmv.parcels_total)} parcels handled · period {inrCompact(d.gmv.period)}</span>} delta={d.gmv.delta_pct} />
            <Kpi testid="kpi-revenue" label="Gross revenue" icon={IndianRupee} value={inrCompact(d.revenue.gross)}
              sub={<span>logistics fees + COD charges</span>} delta={d.revenue.delta_rev_pct} />
            <Kpi testid="kpi-profit" label="Net profit" icon={TrendingUp} accent="text-emerald-500" value={inrCompact(d.revenue.net_profit)}
              sub={<span>after {inrCompact(d.revenue.payout_carriers)} to 3PLs · {inrCompact(d.revenue.payout_franchise)} to hubs</span>} delta={d.revenue.delta_profit_pct} />
            <Kpi testid="kpi-margin" label="Net margin ROI" icon={Percent} accent="text-sky-500" value={pct(d.margin.net_margin_pct, 2)}
              sub={<span>{inr(d.margin.avg_profit_per_order, 2)} profit / order · {num(d.margin.orders)} orders</span>}
              delta={d.margin.prev_margin_pct ? d.margin.net_margin_pct - d.margin.prev_margin_pct : null}>
              <div className="mt-1 h-1.5 bg-muted overflow-hidden"><div className="h-full bg-sky-500" style={{ width: `${Math.min(100, Math.max(0, d.margin.net_margin_pct))}%` }} /></div>
              <div className="text-[10px] text-muted-foreground mt-1">ROI on payouts {pct(d.margin.roi_on_payout_pct)} · avg ticket {inr(d.margin.avg_revenue_per_order)}</div>
            </Kpi>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
            <Panel testid="chart-rev-profit" className="xl:col-span-2 flex flex-col" title="Gross revenue vs net profit"
              subtitle={`${d.granularity === "week" ? "Weekly" : "Daily"} · net profit = revenue − 3PL payouts − franchise commissions`} bodyClass="relative flex-1 min-h-[320px]">
              <div className="absolute inset-3">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={d.series} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })} minTickGap={24} />
                    <YAxis tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => inrCompact(v).replace(".0K", "K")} width={62} />
                    <Tooltip content={<ChartTip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Line type="monotone" dataKey="revenue" name="Gross revenue" stroke="#FF5A1F" strokeWidth={2.2} dot={false} activeDot={{ r: 4 }} />
                    <Line type="monotone" dataKey="profit" name="Net profit" stroke="#10B981" strokeWidth={2.2} dot={false} activeDot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            <Panel testid="widget-cod" title="COD outstanding vs remitted" subtitle="Collected by carriers but not yet paid out vs paid to MSME bank accounts" bodyClass="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-amber-600 dark:text-amber-400 flex items-center gap-1"><Wallet className="h-3 w-3" /> Outstanding</div>
                  <div className="font-heading font-black text-xl font-mono-num" data-testid="cod-outstanding">{inrCompact(cod.outstanding)}</div>
                  <div className="text-[11px] text-muted-foreground">{num(cod.outstanding_parcels)} delivered parcels</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-wide text-emerald-600 dark:text-emerald-400 flex items-center gap-1"><Landmark className="h-3 w-3" /> Remitted</div>
                  <div className="font-heading font-black text-xl font-mono-num" data-testid="cod-remitted">{inrCompact(cod.remitted_total)}</div>
                  <div className="text-[11px] text-muted-foreground">{num(cod.remitted_parcels)} parcels · {inrCompact(cod.remitted_period)} this period</div>
                </div>
              </div>
              <div>
                <div className="h-2.5 bg-amber-500/80 overflow-hidden flex"><div className="h-full bg-emerald-500" style={{ width: `${remPct}%` }} /></div>
                <div className="flex justify-between text-[10px] text-muted-foreground mt-1"><span>{pct(remPct)} remitted</span><span>Still to collect at doorstep: {inrCompact(cod.to_collect)} ({num(cod.to_collect_parcels)})</span></div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1.5">Outstanding ageing (since delivery)</div>
                <div className="space-y-1">
                  {cod.aging.map((a) => (
                    <div key={a.bucket} className="flex items-center gap-2 text-[11px]">
                      <span className="w-16 shrink-0 text-muted-foreground">{a.bucket}</span>
                      <div className="flex-1 h-2 bg-muted"><div className={`h-full ${a.bucket === "15+ days" ? "bg-rose-500" : a.bucket === "8-15 days" ? "bg-orange-500" : "bg-amber-400"}`} style={{ width: `${(a.amount / maxAging) * 100}%` }} /></div>
                      <span className="w-16 text-right font-mono-num">{inrCompact(a.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">Pending remittance by MSME</div>
                <div className="max-h-44 overflow-y-auto divide-y divide-border border border-border">
                  {cod.by_merchant.length === 0 && <div className="p-3 text-xs text-muted-foreground">All COD remitted 🎉</div>}
                  {cod.by_merchant.map((m) => (
                    <div key={m.merchant_id} className="flex items-center gap-2 px-2 py-1.5 text-xs" data-testid={`cod-merchant-${m.merchant_id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{m.merchant_name}</div>
                        <div className="text-[10px] text-muted-foreground">{m.parcels} parcels · oldest {m.oldest_days}d</div>
                      </div>
                      <span className="font-mono-num font-bold">{inrCompact(m.outstanding)}</span>
                      <button onClick={() => remit(m)} data-testid={`cod-remit-${m.merchant_id}`} className="h-7 px-2 bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wide hover:opacity-90">Remit</button>
                    </div>
                  ))}
                </div>
              </div>
            </Panel>
          </div>

          <Panel testid="pipeline" title="Parcel pipeline (period)" subtitle="Click a stage to open those parcels" bodyClass="p-3">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
              {d.pipeline.map((p) => (
                <Link key={p.status} to={`/dashboard/agg/shipments?status=${p.status}`} data-testid={`pipeline-${p.status}`}
                  className={`border px-3 py-2.5 hover:brightness-110 transition ${STATUS_STYLE[p.status]}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wide">{p.label}</div>
                  <div className="font-heading font-black text-2xl font-mono-num text-foreground">{num(p.count)}</div>
                </Link>
              ))}
            </div>
          </Panel>

          <Panel testid="carrier-scorecard" title="3PL carrier scorecard" subtitle="Allocate volume to carriers with the best on-time %, lowest RTO and best margin" bodyClass="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className={TH_ROW}>
                <tr>
                  <SortTh k="carrier_name" label="Carrier" sort={sc} />
                  <SortTh k="shipments" label="Parcels" sort={sc} align="right" />
                  <SortTh k="on_time_pct" label="On-time" sort={sc} align="right" />
                  <SortTh k="rto_pct" label="RTO %" sort={sc} align="right" />
                  <SortTh k="avg_tat_days" label="Avg TAT" sort={sc} align="right" />
                  <SortTh k="cost_per_kg" label="Cost/kg" sort={sc} align="right" />
                  <SortTh k="open_exceptions" label="Open exc." sort={sc} align="right" />
                  <SortTh k="revenue" label="Revenue" sort={sc} align="right" />
                  <SortTh k="profit" label="Profit" sort={sc} align="right" />
                  <SortTh k="margin_pct" label="Margin" sort={sc} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sc.sorted.map((c) => (
                  <tr key={c.carrier_id} className="hover:bg-muted/40" data-testid={`score-row-${c.carrier_id}`}>
                    <td className={`${TD} font-semibold`}>{c.carrier_name}</td>
                    <td className={`${TD} text-right font-mono-num`}>{num(c.shipments)}</td>
                    <td className={`${TD} text-right font-mono-num ${c.on_time_pct >= 90 ? "text-emerald-600 dark:text-emerald-400" : c.on_time_pct < 80 ? "text-rose-600 dark:text-rose-400" : ""}`}>{pct(c.on_time_pct)}</td>
                    <td className={`${TD} text-right font-mono-num ${c.rto_pct >= 20 ? "text-rose-600 dark:text-rose-400" : ""}`}>{pct(c.rto_pct)}</td>
                    <td className={`${TD} text-right font-mono-num`}>{c.avg_tat_days}d</td>
                    <td className={`${TD} text-right font-mono-num`}>{inr(c.cost_per_kg, 1)}</td>
                    <td className={`${TD} text-right font-mono-num`}>{c.open_exceptions || "—"}</td>
                    <td className={`${TD} text-right font-mono-num`}>{inrCompact(c.revenue)}</td>
                    <td className={`${TD} text-right font-mono-num`}>{inrCompact(c.profit)}</td>
                    <td className={`${TD} text-right font-mono-num font-bold`}>{pct(c.margin_pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        </>
      )}
    </div>
  );
}
